// Revision mode's marks in the text (SuiteEditor's RevisionOverlay, RevisionLayoutFragment and the session's
// `setRevisionMarks`; review B17): words added, drawn in their revision's colour; where words were deleted, a small
// caret mark between the letters; and a change bar beside the text column at every line that has either.
//
// The app sets exact marks from `RevisionDiff` (per fragment, UTF-16 offsets in each fragment's text) after the writer
// pauses; between those, every edit shifts them here as the Swift's `RevisionOverlay.edited` does: marks after an edit
// move, typing is marked as added in the current revision (provisionally, until the next diff), and removing text that
// wasn't itself added leaves a deletion mark. Other devices' changes and the documents' own (`External`) move the marks
// without marking anything. A new selection of fragments clears them (the app sends the new one's).
//
// Nothing is stored: the marks are view state, like the annotations.
import { EditorSelection, StateEffect, StateField, type EditorState, type Extension, type Range } from '@codemirror/state';
import {
  Decoration, EditorView, layer, RectangleMarker, WidgetType, type DecorationSet, type LayerMarker, type ViewUpdate,
} from '@codemirror/view';
import { External, layoutField, setLayout } from './state.ts';

/** How many colours the revisions cycle through (terracotta, indigo, green, plum, ochre: `revisionPalette`). */
export const REVISION_COLOURS = 5;

/** Words added in a revision: [from, to) in document positions. */
export interface RevisionInsertion { from: number; to: number; level: number }
/** Where words were deleted: a document position. */
export interface RevisionDeletion { at: number; level: number }

export interface RevisionMarkState {
  /** Whether revision marks are shown at all. */
  on: boolean;
  /** The current revision (new typing takes its colour). */
  level: number;
  /** Sorted by position and never overlapping. */
  insertions: readonly RevisionInsertion[];
  /** Sorted by position. */
  deletions: readonly RevisionDeletion[];
  /** By author (W9c): each level's colour as an entity hue index (0…11, `ENTITY_HUES`; 12 = an unknown device's grey)
   *  instead of the revisions' cycle. */
  hues?: readonly number[];
}

/** A change as `RevisionDiff` gives it (core's `RevisionChange`), in its fragment's UTF-16 offsets. */
export interface RevisionChangeLike {
  current: readonly [number, number];
  base: readonly [number, number];
  levels: readonly { range: readonly [number, number]; level: number }[];
  deletionLevel: number;
}

/** What the app sends: each shown fragment's changes, and the current revision. Null turns the marks off. */
export interface RevisionMarksSpec {
  fragments: { fragmentId: string; changes: readonly RevisionChangeLike[] }[];
  level: number;
  /** Revision marks by author (W9c): levels are writers, each drawn in its entity hue (index into `ENTITY_HUES`, 12 for
   *  an unknown device) rather than a revision's colour. */
  hues?: readonly number[];
}

/** How many entity hues authors are drawn in (the 13th class is an unknown device's grey). */
export const AUTHOR_HUES = 12;

/** The class that colours a mark: its revision's (cycling), or by author its writer's hue. */
export function revisionClass(level: number, hues?: readonly number[]): string {
  if (!hues) return `ws-rev-${level % REVISION_COLOURS}`;
  const hue = hues[level];
  return `ws-rev-by-author ws-rev-hue-${hue === undefined || hue < 0 || hue >= AUTHOR_HUES ? AUTHOR_HUES : hue}`;
}

/** Replaces the marks (exact, from a diff), or turns them off (null). */
export const setRevisionMarks = StateEffect.define<RevisionMarksSpec | null>();

const OFF: RevisionMarkState = { on: false, level: 0, insertions: [], deletions: [] };

/** The first index whose element `isAfter` holds for, in an array where it holds for a suffix. */
function firstIndex<T>(array: readonly T[], isAfter: (t: T) => boolean): number {
  let low = 0, high = array.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (isAfter(array[mid])) high = mid; else low = mid + 1;
  }
  return low;
}

/**
 * `RevisionOverlay.edited`: the marks after an edit at `location` that removed `removed` characters and inserted
 * `inserted` (positions in the text as it stood before this edit, which is the text after the edits before it).
 * With `marking`, typed text is marked as added in the current revision and removed text that wasn't itself added
 * leaves a deletion mark.
 */
export function shiftRevisionMarks(m: RevisionMarkState, location: number, removed: number, inserted: number, marking: boolean): RevisionMarkState {
  const end = location + removed;
  const insertions = m.insertions.slice();
  let removedAdded = 0;
  let absorbed = false;
  let newAt: number | null = null;
  let write = firstIndex(insertions, (x) => x.to >= location);
  for (let read = write; read < insertions.length; read++) {
    const mark = insertions[read];
    const s = mark.from, e = mark.to;
    const overlap = Math.max(0, Math.min(e, end) - Math.max(s, location));
    removedAdded += overlap;
    const length = e - s;
    if (length <= overlap) continue;
    let from = s < location ? s : s >= end ? s - removed : location;
    let len = length - overlap;
    if (inserted > 0) {
      if (from >= location) {
        if (newAt === null) newAt = write;
        from += inserted;
      } else if (from + len > location) {
        // Typing inside an added run lengthens it.
        len += inserted;
        absorbed = true;
      } else if (marking && from + len === location && mark.level === m.level) {
        len += inserted;
        absorbed = true;
      }
    }
    insertions[write++] = { from, to: from + len, level: mark.level };
  }
  insertions.length = write;
  if (marking && inserted > 0 && !absorbed) insertions.splice(newAt ?? write, 0, { from: location, to: location + inserted, level: m.level });
  // Deletion marks after `location`: those removed with the text move to it, later ones back by the removal and on by
  // the insertion. A mark where the typing starts stays before it.
  const deletions = m.deletions.slice();
  const after = firstIndex(deletions, (d) => d.at > location);
  for (let i = after; i < deletions.length; i++) {
    let moved = deletions[i].at >= end ? deletions[i].at - removed : location;
    if (moved > location) moved += inserted;
    deletions[i] = { at: moved, level: deletions[i].level };
  }
  if (marking && removed > removedAdded && !(after > 0 && deletions[after - 1].at === location) && !(after < deletions.length && deletions[after].at === location)) {
    deletions.splice(after, 0, { at: location, level: m.level });
  }
  return { ...m, insertions, deletions };
}

/** The marks for a diff's changes, placed in the document by the layout (fragments not shown are left out). */
function placed(state: EditorState, spec: RevisionMarksSpec): RevisionMarkState {
  const layout = state.field(layoutField);
  const insertions: RevisionInsertion[] = [];
  const deletions: RevisionDeletion[] = [];
  for (const f of spec.fragments) {
    const i = layout.index(f.fragmentId);
    if (i === undefined) continue;
    const body = layout.from[i], length = layout.to[i] - layout.from[i];
    const clamp = (n: number) => body + Math.max(0, Math.min(n, length));
    for (const c of f.changes) {
      for (const part of c.levels) {
        const from = clamp(part.range[0]), to = clamp(part.range[1]);
        if (to > from) insertions.push({ from, to, level: part.level });
      }
      if (c.base[1] > c.base[0]) deletions.push({ at: clamp(c.current[0]), level: c.deletionLevel });
    }
  }
  insertions.sort((a, b) => a.from - b.from || a.to - b.to);
  // Never overlapping (a diff's changes don't; clamping could make them touch).
  const tidy: RevisionInsertion[] = [];
  for (const x of insertions) {
    const last = tidy[tidy.length - 1];
    if (last && x.from < last.to) { if (x.to > last.to) tidy.push({ from: last.to, to: x.to, level: x.level }); } else tidy.push(x);
  }
  deletions.sort((a, b) => a.at - b.at);
  return { on: true, level: spec.level, insertions: tidy, deletions, ...(spec.hues ? { hues: spec.hues } : {}) };
}

class DeletionWidget extends WidgetType {
  constructor(readonly level: number, readonly hues?: readonly number[]) { super(); }
  eq(o: DeletionWidget) { return o.level === this.level && revisionClass(o.level, o.hues) === revisionClass(this.level, this.hues); }
  toDOM() {
    const el = document.createElement('span');
    el.className = `ws-rev-deleted ${revisionClass(this.level, this.hues)}`;
    el.setAttribute('data-revision-level', String(this.level));
    el.setAttribute('aria-hidden', 'true');
    return el;
  }
  ignoreEvent() { return true; }
}

function decorationsOf(m: RevisionMarkState): DecorationSet {
  if (!m.on) return Decoration.none;
  const ranges: Range<Decoration>[] = [];
  for (const x of m.insertions) {
    ranges.push(Decoration.mark({ class: `ws-rev-added ${revisionClass(x.level, m.hues)}`, attributes: { 'data-revision-level': String(x.level) } }).range(x.from, x.to));
  }
  for (const d of m.deletions) ranges.push(Decoration.widget({ widget: new DeletionWidget(d.level, m.hues), side: -1 }).range(d.at));
  return Decoration.set(ranges, true);
}

interface FieldValue { marks: RevisionMarkState; deco: DecorationSet }

export const revisionField = StateField.define<FieldValue>({
  create: () => ({ marks: OFF, deco: Decoration.none }),
  update(value, tr) {
    let marks = value.marks;
    let changed = false;
    for (const e of tr.effects) {
      if (e.is(setRevisionMarks)) {
        marks = e.value ? placed(tr.state, e.value) : OFF;
        changed = true;
      } else if (e.is(setLayout)) {
        // Another selection: the app sends its marks (the current revision stays the same).
        marks = marks.on ? { ...marks, insertions: [], deletions: [] } : OFF;
        changed = true;
      }
    }
    if (tr.docChanged && !changed && marks.on) {
      const marking = !tr.annotation(External);
      tr.changes.iterChanges((fromA, toA, fromB, toB) => {
        marks = shiftRevisionMarks(marks, fromB, toA - fromA, toB - fromB, marking);
      });
      changed = true;
    }
    return changed ? { marks, deco: decorationsOf(marks) } : value;
  },
  provide: (f) => EditorView.decorations.from(f, (v) => v.deco),
});

/** The marks as they stand (document positions). */
export const revisionMarks = (state: EditorState): RevisionMarkState => state.field(revisionField, false)?.marks ?? OFF;

/** The marks by fragment, in each fragment's UTF-16 offsets (tests and tools). */
export function revisionMarksByFragment(state: EditorState): Record<string, { insertions: { from: number; to: number; level: number }[]; deletions: { at: number; level: number }[] }> {
  const layout = state.field(layoutField);
  const m = revisionMarks(state);
  const out: Record<string, { insertions: { from: number; to: number; level: number }[]; deletions: { at: number; level: number }[] }> = {};
  const entry = (i: number) => (out[layout.pieces[i].id] ??= { insertions: [], deletions: [] });
  for (const x of m.insertions) {
    const i = layout.bodyAt(x.from);
    if (i >= 0) entry(i).insertions.push({ from: x.from - layout.from[i], to: x.to - layout.from[i], level: x.level });
  }
  for (const d of m.deletions) {
    const i = layout.bodyAt(d.at);
    if (i >= 0) entry(i).deletions.push({ at: d.at - layout.from[i], level: d.level });
  }
  return out;
}

// MARK: change bars

/** A change bar beside one line of text, in the scroller's content coordinates. */
class BarMarker implements LayerMarker {
  constructor(readonly left: number, readonly top: number, readonly height: number, readonly level: number, readonly colour = `ws-rev-${level % REVISION_COLOURS}`) {}
  eq(o: LayerMarker): boolean {
    return o instanceof BarMarker && o.left === this.left && o.top === this.top && o.height === this.height && o.level === this.level && o.colour === this.colour;
  }
  draw() {
    const el = document.createElement('div');
    this.place(el);
    return el;
  }
  update(dom: HTMLElement, prev: LayerMarker): boolean {
    if (!(prev instanceof BarMarker)) return false;
    this.place(dom);
    return true;
  }
  private place(el: HTMLElement) {
    el.className = `ws-rev-bar ${this.colour}`;
    el.setAttribute('data-revision-level', String(this.level));
    el.style.left = `${this.left}px`;
    el.style.top = `${this.top}px`;
    el.style.height = `${this.height}px`;
  }
}

/** Room between the text column and the bar (`RevisionLayoutFragment.barOffset`). */
const BAR_OFFSET = 14;

function barMarkers(view: EditorView): LayerMarker[] {
  const m = revisionMarks(view.state);
  if (!m.on || (!m.insertions.length && !m.deletions.length)) return [];
  const { from: vFrom, to: vTo } = view.viewport;
  const scroller = view.scrollDOM.getBoundingClientRect();
  const baseLeft = scroller.left - view.scrollDOM.scrollLeft * view.scaleX;
  const content = view.contentDOM.getBoundingClientRect();
  const padding = parseFloat(getComputedStyle(view.contentDOM).paddingLeft) || 0;
  const left = Math.max(1, (content.left - baseLeft) / view.scaleX + padding - BAR_OFFSET);
  // Each visual line a mark touches, once, in the colour of the first mark on it (an added run's before a deletion's).
  const lines: { top: number; height: number; level: number }[] = [];
  const add = (top: number, height: number, level: number) => {
    const middle = top + height / 2;
    if (!lines.some((l) => middle >= l.top && middle <= l.top + l.height)) lines.push({ top, height, level });
  };
  const lineHeight = view.defaultLineHeight;
  const rects = (from: number, to: number, level: number) => {
    for (const r of RectangleMarker.forRange(view, 'x', EditorSelection.range(from, to))) {
      // A range over several lines comes back as its first line, the block between and its last: split the block.
      const count = Math.max(1, Math.round(r.height / lineHeight));
      const h = r.height / count;
      for (let k = 0; k < count; k++) add(r.top + k * h, h, level);
    }
  };
  for (const x of m.insertions) if (x.to > vFrom && x.from < vTo) rects(x.from, x.to, x.level);
  const doc = view.state.doc;
  for (const d of m.deletions) {
    if (d.at < vFrom || d.at > vTo) continue;
    // The character beside the mark, measured as text is (a line's box, not the caret's).
    const line = doc.lineAt(d.at);
    if (line.from === line.to) continue;
    const [a, b] = d.at < line.to ? [d.at, d.at + 1] : [d.at - 1, d.at];
    const r = RectangleMarker.forRange(view, 'x', EditorSelection.range(a, b))[0];
    if (r) add(r.top, r.height, d.level);
  }
  return lines.sort((a, b) => a.top - b.top).map((l) => new BarMarker(left, l.top + 2, Math.max(4, l.height - 4), l.level, revisionClass(l.level, m.hues)));
}

const bars = layer({
  above: false,
  class: 'ws-rev-bars',
  markers: barMarkers,
  update: (u: ViewUpdate) =>
    u.docChanged || u.viewportChanged || u.geometryChanged || u.startState.field(revisionField, false) !== u.state.field(revisionField, false),
});

/** Revision marks: the field (added words and deletion marks as decorations) and the change bars. */
export function revisions(): Extension {
  return [revisionField, bars];
}
